import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services';
import { Loader2, Building2, Search, Plus } from 'lucide-react';

function FleetPartnerHospitals() {
  const { user } = useAuth();
  const [partnerships, setPartnerships] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [inviting, setInviting] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [removingRequest, setRemovingRequest] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [itemToRemove, setItemToRemove] = useState({ id: null, type: null });

  // Separate state for invite form search
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [inviteSearchLoading, setInviteSearchLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    hospitalName: '',
    hospitalEmail: '',
    hospitalPhone: '',
    hospitalAddress: ''
  });

  // Get fleet ID from user data
  const fleetId = user?.operator_id || user?.fleet_id;

  useEffect(() => {
    const fetchPartnerships = async () => {
      if (!fleetId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🏥 Fetching partnerships and pending requests for fleet:', fleetId);

        // Fetch active partnerships
        let partnershipsData = [];
        try {
          const partnershipsResponse = await authService.fleetGetPartnerships(fleetId);
          console.log('🔄 Partnerships API Response:', partnershipsResponse);

          if (partnershipsResponse.success && partnershipsResponse.data) {
            partnershipsData = partnershipsResponse.data.partnerships || [];
            setPartnerships(partnershipsData);
            console.log('✅ Active partnerships loaded:', partnershipsData.length);
            console.log('📋 Partnership statuses:', partnershipsData.map(p => ({ name: p.hospital?.name, status: p.status })));
          } else {
            console.warn('⚠️ Partnerships API call failed or returned no data:', partnershipsResponse);
          }
        } catch (partnershipsError) {
          console.error('❌ Error fetching partnerships:', partnershipsError);
        }

        // Fetch pending partnership requests
        let pendingRequestsData = [];
        try {
          const requestsResponse = await authService.fleetGetPartnershipRequests(fleetId, {
            status: undefined,
            limit: 50
          });
          console.log('🔄 Requests API Response:', requestsResponse);
          console.log('🔄 Full Response Data:', JSON.stringify(requestsResponse?.data, null, 2));

          // ALSO try fetching invitations to see if that's what we need
          console.log('🔄 TESTING: Also fetching invitations...');
          try {
            const invitationsResponse = await authService.fleetGetPartnershipInvitations(fleetId, {
              limit: 50
            });
            console.log('🔄 Invitations API Response:', invitationsResponse);
            console.log('🔄 Invitations Data:', JSON.stringify(invitationsResponse?.data, null, 2));
          } catch (invErr) {
            console.log('🔄 Invitations fetch failed:', invErr);
          }

          if (requestsResponse.success && requestsResponse.data) {
            pendingRequestsData = requestsResponse.data.requests || [];
            setPendingRequests(pendingRequestsData);
            console.log('✅ Pending requests loaded:', pendingRequestsData.length);
            console.log('📋 Pending requests:', pendingRequestsData.map(p => ({ name: p.hospital?.name, status: p.status })));
            console.log('🔍 Raw requests data:', pendingRequestsData);
          } else {
            console.warn('⚠️ Requests API call failed or returned no data:', requestsResponse);
          }
        } catch (requestsError) {
          console.error('❌ Error fetching pending requests:', requestsError);
        }

        console.log('🏁 Final data loaded - Partnerships:', partnershipsData.length, 'Requests:', pendingRequestsData.length);
      } catch (err) {
        console.error('❌ General error in fetch process:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerships();
  }, [fleetId]);

  // Combine partnerships and pending requests for unified view
  const allItems = [
    ...partnerships.map(p => ({ ...p, type: 'partnership' })),
    ...pendingRequests.map(r => ({
      ...r,
      type: 'request',
      // Map request fields to partnership-like structure for consistency
      hospital: {
        ...r.hospital,
        // Use hospital details from message if hospital name is empty
        name: r.hospital?.name || r.message?.hospital_details?.name || 'Unknown Hospital',
        address: r.hospital?.address || r.message?.hospital_details?.address || '',
        email: r.hospital?.email || r.message?.hospital_details?.email || '',
        phone: r.hospital?.phone || r.message?.hospital_details?.phone || ''
      },
      status: r.status || 'PENDING',
      partnership_id: r.request_id,
      // Extract message text
      message: typeof r.message === 'string' ? r.message : r.message?.message || 'Partnership request'
    }))
  ];

  // Filter combined items based on active filter
  const filteredPartnerships = allItems.filter(item => {
    const matchesFilter = activeFilter === 'All' ||
                         (activeFilter === 'Pending' && item.status?.toUpperCase() === 'PENDING') ||
                         (activeFilter === 'Active' && (item.status?.toUpperCase() === 'ACTIVE' || item.status?.toUpperCase() === 'APPROVED')) ||
                         (activeFilter === 'Invited' && item.status?.toUpperCase() === 'INVITED') ||
                         (activeFilter !== 'All' && activeFilter !== 'Pending' && activeFilter !== 'Active' && activeFilter !== 'Invited' && item.status?.toUpperCase() === activeFilter.toUpperCase());
    const matchesSearch = !searchQuery ||
                         item.hospital?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Debug logging for filter issues
  console.log('🔍 Filter Debug:', {
    activeFilter,
    totalPartnerships: partnerships.length,
    totalPendingRequests: pendingRequests.length,
    totalItems: allItems.length,
    filteredCount: filteredPartnerships.length,
    allStatuses: allItems.map(p => ({ type: p.type, status: p.status, name: p.hospital?.name })),
    searchQuery,
    pendingRequestsRaw: pendingRequests,
    allItemsDetailed: allItems
  });

  // Handle hospital search
  const handleHospitalSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    try {
      setSearchLoading(true);
      console.log('🔍 Searching hospitals:', query);

      const response = await authService.fleetGetHospitals(fleetId, {
        search: query,
        limit: 10
      });

      if (response.success && response.data) {
        const hospitals = response.data.hospitals || [];
        setSearchResults(hospitals);
        setShowSearchDropdown(hospitals.length > 0);
        console.log('✅ Hospital search results:', hospitals.length);
      }
    } catch (error) {
      console.error('❌ Error searching hospitals:', error);
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce search
    if (value !== searchQuery) {
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        handleHospitalSearch(value);
      }, 300);
    }
  };

  // Handle hospital search for invite form
  const handleInviteHospitalSearch = async (query) => {
    if (!query || query.length < 2) {
      setInviteSearchResults([]);
      setShowInviteDropdown(false);
      return;
    }

    try {
      setInviteSearchLoading(true);
      console.log('🔍 Searching hospitals for invite form:', query);

      const response = await authService.fleetGetHospitals(fleetId, {
        search: query,
        limit: 10
      });

      if (response.success && response.data) {
        const hospitals = response.data.hospitals || [];
        setInviteSearchResults(hospitals);
        setShowInviteDropdown(hospitals.length > 0);
        console.log('✅ Invite form hospital search results:', hospitals.length);
      }
    } catch (error) {
      console.error('❌ Error searching hospitals for invite:', error);
      setInviteSearchResults([]);
      setShowInviteDropdown(false);
    } finally {
      setInviteSearchLoading(false);
    }
  };

  // Handle hospital selection from main search dropdown
  const handleMainSearchHospitalSelect = (hospital) => {
    setSearchQuery(hospital.name);
    setShowSearchDropdown(false);
    setSearchResults([]);
  };

  // Handle hospital selection from invite dropdown
  const handleInviteHospitalSelect = (hospital) => {
    setInviteForm(prev => ({
      ...prev,
      hospitalName: hospital.name,
      hospitalEmail: hospital.email || '',
      hospitalPhone: hospital.phone || '',
      hospitalAddress: hospital.address || ''
    }));
    setShowInviteDropdown(false);
    setInviteSearchResults([]);
  };

  const handleInviteHospital = async (e) => {
    e.preventDefault();
    if (!fleetId || !inviteForm.hospitalName || !inviteForm.hospitalEmail || !inviteForm.hospitalPhone || !inviteForm.hospitalAddress) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setInviting(true);
      console.log('🚛 Inviting hospital:', inviteForm);

      const inviteData = {
        hospital_name: inviteForm.hospitalName,
        hospital_email: inviteForm.hospitalEmail,
        hospital_phone: inviteForm.hospitalPhone,
        hospital_address: inviteForm.hospitalAddress,
        message: `We would like to partner with your hospital for emergency ambulance services.`
      };

      const response = await authService.fleetInviteHospital(fleetId, inviteData);

      if (response.success) {
        alert('Hospital invitation sent successfully!');
        setInviteForm({ hospitalName: '', hospitalEmail: '', hospitalPhone: '', hospitalAddress: '' });
        setSearchQuery('');

        // Refresh partnerships and pending requests with detailed logging
        console.log('🔄 Refreshing partnerships and pending requests after successful invitation...');
        try {
          const updatedPartnershipsResponse = await authService.fleetGetPartnerships(fleetId);
          const updatedRequestsResponse = await authService.fleetGetPartnershipRequests(fleetId, {
            status: undefined,
            limit: 50
          });

          console.log('🔄 Partnership refresh response:', updatedPartnershipsResponse);
          console.log('🔄 Requests refresh response:', updatedRequestsResponse);

          if (updatedPartnershipsResponse.success && updatedPartnershipsResponse.data) {
            const newPartnerships = updatedPartnershipsResponse.data.partnerships || [];
            console.log('✅ Setting new partnerships:', newPartnerships.length);
            console.log('📋 New partnership statuses:', newPartnerships.map(p => ({ name: p.hospital?.name, status: p.status })));
            setPartnerships(newPartnerships);
          } else {
            console.error('❌ Partnership refresh failed:', updatedPartnershipsResponse);
          }

          if (updatedRequestsResponse.success && updatedRequestsResponse.data) {
            const newRequests = updatedRequestsResponse.data.requests || [];
            console.log('✅ Setting new pending requests:', newRequests.length);
            console.log('📋 New pending request statuses:', newRequests.map(p => ({ name: p.hospital?.name, status: p.status })));
            setPendingRequests(newRequests);
          } else {
            console.error('❌ Requests refresh failed:', updatedRequestsResponse);
          }
        } catch (refreshError) {
          console.error('❌ Error refreshing partnerships and requests:', refreshError);
        }
      }
    } catch (error) {
      console.error('❌ Error inviting hospital:', error);
      alert(`Failed to send invitation: ${error.message}`);
    } finally {
      setInviting(false);
    }
  };

  // Handle approving a partnership request
  const handleApproveRequest = async (requestId) => {
    if (!fleetId || !requestId) return;

    if (!confirm('Are you sure you want to approve this partnership request?')) {
      return;
    }

    try {
      setApprovingRequest(requestId);
      console.log('✅ Approving partnership request:', requestId);

      const response = await authService.fleetRespondToInvitation(fleetId, requestId, {
        response: 'ACCEPT',
        message: 'Partnership request approved by fleet administrator'
      });

      if (response.success) {
        console.log('✅ Partnership request approved successfully');
        console.log('✅ Approval response details:', response);

        // Wait a moment for backend to process, then refresh
        setTimeout(async () => {
          await refreshData();
        }, 1000);

        alert('Partnership request approved successfully!');
      } else {
        console.error('❌ Approval failed:', response);
        alert(`Failed to approve: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error approving partnership request:', error);
      alert(`Failed to approve partnership request: ${error.message}`);
    } finally {
      setApprovingRequest(null);
    }
  };

  // Handle rejecting a partnership request
  const handleRejectRequest = async (requestId) => {
    if (!fleetId || !requestId) return;

    if (!confirm('Are you sure you want to reject this partnership request?')) {
      return;
    }

    try {
      setRejectingRequest(requestId);
      console.log('❌ Rejecting partnership request:', requestId);

      const response = await authService.fleetRespondToInvitation(fleetId, requestId, {
        response: 'REJECT',
        message: 'Partnership request rejected by fleet administrator'
      });

      if (response.success) {
        console.log('✅ Partnership request rejected successfully');
        // Refresh the data
        await refreshData();
        alert('Partnership request rejected successfully!');
      }
    } catch (error) {
      console.error('❌ Error rejecting partnership request:', error);
      alert(`Failed to reject partnership request: ${error.message}`);
    } finally {
      setRejectingRequest(null);
    }
  };

  // Handle showing remove confirmation dialog
  const handleRemoveRequest = (itemId, itemType = 'request') => {
    if (!fleetId || !itemId) return;
    setItemToRemove({ id: itemId, type: itemType });
    setShowRemoveDialog(true);
  };

  // Handle actual removal after confirmation
  const confirmRemoveRequest = async () => {
    const { id: itemId, type: itemType } = itemToRemove;
    if (!itemId) return;

    setShowRemoveDialog(false);

    try {
      setRemovingRequest(itemId);
      console.log('🗑️ Removing item:', { itemId, itemType });

      const response = await authService.fleetDeletePartnershipRequest(fleetId, itemId);

      if (response.success) {
        console.log('✅ Item removed successfully');
        // Refresh the data
        await refreshData();
        const successMessage = itemType === 'request'
          ? 'Partnership request deleted successfully!'
          : 'Partnership removed successfully!';
        // Success will be shown by the data refresh
      }
    } catch (error) {
      console.error('❌ Error removing item:', error);
      // Error will be handled by the UI
    } finally {
      setRemovingRequest(null);
      setItemToRemove({ id: null, type: null });
    }
  };

  // Helper function to refresh all data
  const refreshData = async () => {
    if (!fleetId) return;

    try {
      // Fetch active partnerships
      const partnershipsResponse = await authService.fleetGetPartnerships(fleetId);

      // Fetch ALL partnership requests (not just pending ones)
      const requestsResponse = await authService.fleetGetPartnershipRequests(fleetId, {
        status: undefined,
        limit: 50
      });

      if (partnershipsResponse.success && partnershipsResponse.data) {
        const partnerships = partnershipsResponse.data.partnerships || [];
        setPartnerships(partnerships);
        console.log('✅ Refreshed active partnerships:', partnerships.length);
        console.log('📋 Partnership details:', partnerships.map(p => ({
          id: p.partnership_id,
          name: p.hospital?.name,
          status: p.status
        })));
      }

      if (requestsResponse.success && requestsResponse.data) {
        const allRequests = requestsResponse.data.requests || [];
        // Keep all requests for now - we'll handle the display logic in the UI
        setPendingRequests(allRequests);

        console.log('📡 RAW API RESPONSE:', JSON.stringify(requestsResponse.data, null, 2));
        console.log('✅ Refreshed all requests:', allRequests.length);
        console.log('📋 All request details with full status info:', allRequests.map(r => ({
          id: r.request_id,
          name: r.hospital?.name || r.message?.hospital_details?.name,
          status: r.status,
          rawStatus: JSON.stringify(r.status),
          fullItem: r
        })));

        // Log approved vs pending requests for debugging
        const approvedRequests = allRequests.filter(r => {
          const isApproved = r.status === 'APPROVED' || r.status?.toUpperCase() === 'APPROVED';
          console.log(`🔍 Checking request ${r.request_id}: status="${r.status}", isApproved=${isApproved}`);
          return isApproved;
        });
        const pendingRequests = allRequests.filter(r => r.status === 'PENDING');
        const rejectedRequests = allRequests.filter(r => r.status === 'REJECTED');

        console.log('✅ Found approved requests:', approvedRequests.length);
        console.log('✅ Found pending requests:', pendingRequests.length);
        console.log('✅ Found rejected requests:', rejectedRequests.length);

        if (approvedRequests.length > 0) {
          console.log('📋 Approved requests details:', approvedRequests.map(r => ({
            id: r.request_id,
            name: r.hospital?.name || r.message?.hospital_details?.name,
            status: r.status,
            fullRecord: r
          })));
        }
      } else {
        console.error('❌ Requests API failed:', requestsResponse);
      }
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
    }
  };

  return (
    <div className="p-6 max-w-8xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Partner Hospitals</h1>
        <p className="text-sm text-gray-500">Search, invite, and manage partnered hospitals.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow p-4">
            <label className="block text-sm font-medium mb-2">Search hospitals</label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    className="w-full border rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                    placeholder="Type hospital name"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onFocus={() => {
                      if (searchResults.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding dropdown to allow clicks
                      setTimeout(() => setShowSearchDropdown(false), 200);
                    }}
                  />
                  {searchLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
                <button className="px-4 py-2 rounded-lg bg-blue-600 text-white flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>

              {/* Search Results Dropdown */}
              {showSearchDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                  {searchResults.map((hospital, index) => (
                    <div
                      key={hospital.hospital_id || hospital.id || index}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handleMainSearchHospitalSelect(hospital)}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="w-8 h-8 text-blue-600 bg-blue-100 rounded-lg p-1.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {hospital.name || 'Unknown Hospital'}
                          </div>
                          {hospital.address && (
                            <div className="text-sm text-gray-500 truncate">
                              {hospital.address}
                            </div>
                          )}
                          {hospital.email && (
                            <div className="text-xs text-gray-400">
                              {hospital.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-2">If not found, invite the hospital below.</div>
          </div>

          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-medium">Your partnerships ({filteredPartnerships.length})</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    console.log('🔄 Manual refresh clicked');
                    const fetchPartnerships = async () => {
                      try {
                        setLoading(true);
                        console.log('🏥 Manual fetching partnerships and pending requests for fleet:', fleetId);

                        // Fetch active partnerships
                        const partnershipsResponse = await authService.fleetGetPartnerships(fleetId);

                        // Fetch pending partnership requests
                        const requestsResponse = await authService.fleetGetPartnershipRequests(fleetId, {
                          status: undefined,
                          limit: 50
                        });

                        if (partnershipsResponse.success && partnershipsResponse.data) {
                          const partnerships = partnershipsResponse.data.partnerships || [];
                          setPartnerships(partnerships);
                          console.log('✅ Manual active partnerships loaded:', partnerships.length);
                          console.log('📋 Manual partnership statuses:', partnerships.map(p => ({ name: p.hospital?.name, status: p.status })));
                        }

                        if (requestsResponse.success && requestsResponse.data) {
                          const pendingRequests = requestsResponse.data.requests || [];
                          setPendingRequests(pendingRequests);
                          console.log('✅ Manual pending requests loaded:', pendingRequests.length);
                          console.log('📋 Manual pending requests:', pendingRequests.map(p => ({ name: p.hospital?.name, status: p.status })));
                        }
                      } catch (err) {
                        console.error('❌ Manual error fetching partnerships:', err);
                      } finally {
                        setLoading(false);
                      }
                    };
                    fetchPartnerships();
                  }}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                >
                  🔄 Refresh
                </button>
                <div className="flex gap-2 text-sm">
                {['All', 'Active', 'Pending', 'Invited'].map(filter => {
                  const count = filter === 'All'
                    ? allItems.length
                    : filter === 'Pending'
                      ? allItems.filter(item => item.status?.toUpperCase() === 'PENDING').length
                      : filter === 'Active'
                        ? allItems.filter(item => item.status?.toUpperCase() === 'ACTIVE' || item.status?.toUpperCase() === 'APPROVED').length
                        : allItems.filter(item => item.status?.toUpperCase() === filter.toUpperCase()).length;

                  return (
                    <button
                      key={filter}
                      onClick={() => setActiveFilter(filter)}
                      className={`px-3 py-1 rounded ${
                        activeFilter === filter
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter} ({count})
                    </button>
                  );
                })}
                </div>
              </div>
            </div>

            <div className="divide-y">
              {loading ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading partnerships...</p>
                </div>
              ) : filteredPartnerships.length > 0 ? (
                filteredPartnerships.map((item, index) => (
                  <div key={item.partnership_id || item.request_id || index} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg p-1.5 ${
                        item.type === 'request'
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        <Building2 className="w-full h-full" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.hospital?.name || 'Unknown Hospital'}</span>
                          {item.type === 'request' && (
                            <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                              Request
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.type === 'request' && item.status === 'PENDING' && 'Pending partnership request • awaiting your approval'}
                          {item.type === 'request' && item.status === 'APPROVED' && 'Approved partnership • ready for use'}
                          {item.type === 'request' && item.status === 'REJECTED' && 'Rejected partnership request'}
                          {item.type === 'partnership' && item.status === 'ACTIVE' && 'Active partnership • selectable during booking'}
                          {item.type === 'partnership' && item.status === 'PENDING' && 'Pending invitation • awaiting hospital acceptance'}
                          {item.type === 'partnership' && item.status === 'INVITED' && 'Invited • awaiting hospital response'}
                          {(!item.status || !item.type) && 'Status unknown'}
                        </div>
                        {item.hospital?.address && (
                          <div className="text-xs text-gray-400 mt-1">{item.hospital.address}</div>
                        )}
                        {item.type === 'request' && item.message && (
                          <div className="text-xs text-gray-600 mt-1 italic">"{item.message}"</div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : item.status === 'APPROVED'
                          ? 'bg-green-100 text-green-700'
                          : item.status === 'PENDING' && item.type === 'request'
                          ? 'bg-orange-100 text-orange-700'
                          : item.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-700'
                          : item.status === 'REJECTED'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {item.status === 'APPROVED' ? 'active' : (item.status?.toLowerCase() || 'unknown')}
                      </span>
                      {item.type === 'request' && item.status === 'PENDING' && (
                        <button
                          className="px-3 py-1 rounded bg-red-100 text-red-700 text-sm hover:bg-red-200 disabled:opacity-50"
                          onClick={() => handleRemoveRequest(item.request_id)}
                          disabled={removingRequest === item.request_id}
                        >
                          {removingRequest === item.request_id ? 'Removing...' : 'Remove'}
                        </button>
                      )}
                      {item.type === 'partnership' && item.status === 'PENDING' && (
                        <button className="px-3 py-1 rounded bg-gray-100 text-sm hover:bg-gray-200">
                          Resend
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-gray-900 font-medium">No partnerships found</h3>
                  <p className="text-gray-500 text-sm">
                    {searchQuery ? 'Try adjusting your search criteria' : 'Start by inviting hospitals to partner with your fleet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Invite hospital
            </h3>
            <form onSubmit={handleInviteHospital} className="space-y-3">
              <div className="relative">
                <input
                  className="w-full border rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                  placeholder="Hospital name *"
                  value={inviteForm.hospitalName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInviteForm(prev => ({ ...prev, hospitalName: value }));

                    // Trigger search for invite form using separate handler
                    if (value !== inviteForm.hospitalName) {
                      clearTimeout(window.inviteSearchTimeout);
                      window.inviteSearchTimeout = setTimeout(() => {
                        handleInviteHospitalSearch(value);
                      }, 300);
                    }
                  }}
                  onFocus={() => {
                    if (inviteSearchResults.length > 0) {
                      setShowInviteDropdown(true);
                    }
                  }}
                  onBlur={() => {
                    // Delay hiding dropdown to allow clicks
                    setTimeout(() => setShowInviteDropdown(false), 200);
                  }}
                  required
                />
                {inviteSearchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}

                {/* Hospital Dropdown for Invite Form */}
                {showInviteDropdown && inviteSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                    {inviteSearchResults.map((hospital, index) => (
                      <div
                        key={hospital.hospital_id || hospital.id || index}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => handleInviteHospitalSelect(hospital)}
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className="w-8 h-8 text-blue-600 bg-blue-100 rounded-lg p-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {hospital.name || 'Unknown Hospital'}
                            </div>
                            {hospital.address && (
                              <div className="text-sm text-gray-500 truncate">
                                {hospital.address}
                              </div>
                            )}
                            {hospital.email && (
                              <div className="text-xs text-gray-400">
                                {hospital.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <input
                className="w-full border rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                type="email"
                placeholder="Hospital email *"
                value={inviteForm.hospitalEmail}
                onChange={(e) => setInviteForm(prev => ({ ...prev, hospitalEmail: e.target.value }))}
                required
              />
              <input
                className="w-full border rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none"
                type="tel"
                placeholder="Hospital phone *"
                value={inviteForm.hospitalPhone}
                onChange={(e) => setInviteForm(prev => ({ ...prev, hospitalPhone: e.target.value }))}
                required
              />
              <textarea
                className="w-full border rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none resize-none"
                placeholder="Hospital address *"
                value={inviteForm.hospitalAddress}
                onChange={(e) => setInviteForm(prev => ({ ...prev, hospitalAddress: e.target.value }))}
                rows={3}
                required
              />
              <button
                type="submit"
                disabled={inviting || !inviteForm.hospitalName || !inviteForm.hospitalEmail || !inviteForm.hospitalPhone || !inviteForm.hospitalAddress}
                className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {inviting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Send invite
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500">We will notify the hospital and update status here.</p>
            </form>
          </div>
          <div className="bg-white rounded-xl shadow p-4">
            <h3 className="font-medium mb-2">How it works</h3>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
              <li>Search existing hospitals or invite new ones</li>
              <li>Super admin approval may apply in some cases</li>
              <li>Hospital must accept before partnership is active</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Remove Confirmation Dialog */}
      {showRemoveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {itemToRemove.type === 'request' ? 'Delete Partnership Request' : 'Remove Partnership'}
            </h3>
            <p className="text-gray-600 mb-6">
              {itemToRemove.type === 'request'
                ? 'Are you sure you want to delete this partnership request? This action cannot be undone.'
                : 'Are you sure you want to remove this partnership? This action cannot be undone.'
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRemoveDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={removingRequest === itemToRemove.id}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveRequest}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                disabled={removingRequest === itemToRemove.id}
              >
                {removingRequest === itemToRemove.id ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FleetPartnerHospitals


